import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { UserRole } from '@bridgewerk/shared';
import { useAuth } from '../features/auth/useAuth.js';

export type WorkMode = 'manager' | 'programmer' | 'operator';

export interface ImpersonationTarget {
  readonly userId: string;
  readonly role: UserRole;
  /** null = impersonated user is a manager (sees all machines) */
  readonly assignedMachineIds: ReadonlySet<string> | null;
}

export interface ModeContextValue {
  /** The mode currently being rendered (may differ from the user's real role during preview). */
  readonly currentMode: WorkMode;
  /** Modes the authenticated user is allowed to switch to. */
  readonly availableModes: readonly WorkMode[];
  readonly setMode: (mode: WorkMode) => void;
  /** Non-null when a manager is viewing as another user. */
  readonly impersonation: ImpersonationTarget | null;
  readonly startImpersonating: (target: ImpersonationTarget) => void;
  readonly stopImpersonating: () => void;
  /** Role used for access-control checks (impersonated role if active). */
  readonly effectiveRole: UserRole;
  /** Machine IDs visible in the current view. null = all machines. */
  readonly effectiveMachineIds: ReadonlySet<string> | null;
}

const ModeContext = createContext<ModeContextValue | null>(null);

function defaultModeForRole(role: UserRole): WorkMode {
  if (role === 'operator') return 'operator';
  if (role === 'programmer') return 'programmer';
  return 'manager';
}

function availableModesForRole(role: UserRole): readonly WorkMode[] {
  if (role === 'operator') return ['operator'] as const;
  if (role === 'programmer') return ['programmer', 'operator'] as const;
  return ['manager', 'programmer', 'operator'] as const;
}

export function ModeProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const { role, assignedMachineIds, isManager } = useAuth();
  const effectiveRole: UserRole = role ?? 'manager';

  const [currentMode, setCurrentMode] = useState<WorkMode>(() => defaultModeForRole(effectiveRole));
  const [impersonation, setImpersonation] = useState<ImpersonationTarget | null>(null);

  const setMode = useCallback((mode: WorkMode) => {
    const allowed = availableModesForRole(effectiveRole);
    if (allowed.includes(mode)) setCurrentMode(mode);
  }, [effectiveRole]);

  const startImpersonating = useCallback((target: ImpersonationTarget) => {
    if (!isManager) return;
    setImpersonation(target);
    setCurrentMode(defaultModeForRole(target.role));
  }, [isManager]);

  const stopImpersonating = useCallback(() => {
    setImpersonation(null);
    setCurrentMode(defaultModeForRole(effectiveRole));
  }, [effectiveRole]);

  const value = useMemo<ModeContextValue>(() => {
    const active = impersonation;
    return {
      currentMode,
      availableModes: availableModesForRole(effectiveRole),
      setMode,
      impersonation: active,
      startImpersonating,
      stopImpersonating,
      effectiveRole: active ? active.role : effectiveRole,
      effectiveMachineIds: active ? active.assignedMachineIds : assignedMachineIds,
    };
  }, [currentMode, effectiveRole, setMode, impersonation, startImpersonating, stopImpersonating, assignedMachineIds]);

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error('useMode must be used inside <ModeProvider>');
  return ctx;
}
